import { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import RefreshRounded from '@mui/icons-material/RefreshRounded';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import SearchBar from 'shared/components/search-bar/search-bar';
import { FilterChipOption } from 'shared/components/table-filter-chips/multi-value-filter-chip/models/filter-chip-option';
import MultiValueFilterChip from 'shared/components/table-filter-chips/multi-value-filter-chip/multi-value-filter-chip';
import OrganisationUnitFilterChip from 'shared/components/table-filter-chips/organisation-unit-filter-chip/organisation-unit-filter-chip';
import SelectedOrganisationTreeNode from 'shared/features/organisation/models/selected-organisation-tree-node';
import { OrganisationUnitTreeProvider } from 'shared/features/organisation/providers/organisation-unit-tree-provider';

import { useSystemsContextProvider } from 'features/systems/context/systems-provider';
import { SystemType } from 'features/systems/domain/models/system';
import { SystemFilterValueType } from 'features/systems/domain/models/system-filter-value';
import styles from 'features/systems/views/components/system-filters.module.scss';
import SystemMonitoringFilter from 'features/systems/views/components/system-monitoring-filter';

export default function SystemFilters(): ReactElement {
  const { t } = useTranslation('systems');

  const { refresh, systemsFilterHook } = useSystemsContextProvider();

  const getTypeFilterValues = (): FilterChipOption[] =>
    Object.entries(SystemType)
      .filter(([_, value]) => value !== SystemType.Unknown)
      .map(([key, value]) => ({
        key,
        value: [key],
        label: t(`filterValues.type.${value}`),
      }));

  return (
    <div className={styles.container}>
      <div className={styles.topRow}>
        <div className={styles.systemFilters}>
          <SearchBar onSearchValueChanged={systemsFilterHook.submitSearch} searchValue={systemsFilterHook.searchBarValue} />
          <MultiValueFilterChip
            options={getTypeFilterValues()}
            onOptionsSelected={(keys: string[]) => systemsFilterHook.selectOption(SystemFilterValueType.Type, keys)}
            placeHolder={t('filters.type')}
            selectedOptions={getTypeFilterValues().filter((x) =>
              systemsFilterHook.activeFilters.find((x) => x.filterValueType === SystemFilterValueType.Type)?.values.includes(x.key),
            )}
          />
          <OrganisationUnitTreeProvider.All>
            <OrganisationUnitFilterChip
              onOptionsSelected={(keys: SelectedOrganisationTreeNode[]) => {
                systemsFilterHook.changeSelectedOrganisationUnits(keys);
                systemsFilterHook.selectOption(
                  SystemFilterValueType.OrganisationUnit,
                  keys.map((x) => x.id),
                );
              }}
              selectedOptions={systemsFilterHook.selectedOrganisationUnits}
              placeHolder={t('filters.organisationUnits')}
            />
          </OrganisationUnitTreeProvider.All>

          <div className={styles.clearFilters}>
            <Button variant="text" color="primary" onClick={systemsFilterHook.clearFilters}>
              {t('filters.clearFilters')}
            </Button>
          </div>
        </div>

        {refresh && (
          <IconButton className={styles.refreshButton} onClick={refresh}>
            <RefreshRounded />
          </IconButton>
        )}
      </div>

      {systemsFilterHook.monitorFiltersIsSuccess && systemsFilterHook.monitorFilters.length > 0 && (
        <div className={styles.monitoringFilters}>
          <SystemMonitoringFilter.Offline />
          <SystemMonitoringFilter.Warning />
          <SystemMonitoringFilter.Online />
        </div>
      )}
    </div>
  );
}
